import { db } from "@innoclouds/db";
import { subscribeOrgUpdates } from "@innoclouds/service-worker/broadcast";
import type { SWMessage } from "@innoclouds/service-worker/messages";
import i18next from "i18next";
import { isMockMode, isPreviewMode } from "#/preview";
import { InitConnection } from "#/requests";
import { bindSocketStatusReporting } from "#/sdk";
import { useCardStore } from "#/store/card";
import { useHotmenuStore } from "#/store/hotmenu";
import { useLikesStore } from "#/store/likes";
import { useMainStore } from "#/store/main";
import { useModulesStore } from "#/store/modules";
import { useThemeStore } from "#/store/theme";
import { addToCache } from "#/utils/cache";
import { emitter } from "#/utils/emitter";
import { getLocaleName } from "#/utils/i18next";
import { getWebSocketInstance } from "#/utils/WebSocketService";

const SW_READY_TIMEOUT_MS = 2000;
const SW_ACK_TIMEOUT_MS = 2000;
const KEEP_ALIVE_INTERVAL_MS = 20_000;
const SYNC_INTERVAL = 60_000 * 15; // 15 minutes

type SyncRequestOptions = { syncMenu?: boolean; syncCombos?: boolean };

type PeriodicSync = {
	requestSync: (syncOptions?: SyncRequestOptions) => Promise<void>;
	stop: () => void;
};

interface Options {
	orgId: string;
	domain: string;
	wsUrl?: string;
	forceSync?: boolean;
	cacheMedia?: boolean;
	withCombos?: boolean;
}

enum PrefetchStatus {
	STARTED = "started",
	COMPLETED = "completed",
	CANCELLED = "cancelled",
}

type BroadcastMessageData =
	| {
			type: "MENU_UPDATE";
			orgId: string;
			groups_added: string[];
			groups_deleted: string[];
			groups_updated: string[];
			products_added: string[];
			products_deleted: string[];
			products_updated: string[];
	  }
	| { type: "COMBO_UPDATE"; orgId: string; added: string[]; deleted: string[]; updated: string[] }
	| { type: "ORG_UNIT_UPDATE"; orgId: string; params: Record<string, unknown> }
	| { type: "ORGANIZATION_UPDATE"; orgId: string; params: Record<string, unknown> }
	| { type: "DESIGN_CONFIG_UPDATE"; orgId: string; params: Record<string, unknown> }
	| { type: "DECORATION_UPDATE"; orgId: string; params: Record<string, unknown> }
	| { type: "SCHEDULE_UPDATE"; orgId: string; params: Record<string, unknown> }
	| { type: "BADGES_UPDATE"; orgId: string; params: Record<string, unknown> }
	| { type: "PREFETCH_STATUS"; orgId: string; status: PrefetchStatus }
	| { type: "PREFETCH_PROGRESS"; orgId: string; total: number; done: number; phase: "groups" | "products" };

type SyncTransport = {
	post(message: SWMessage): Promise<void>;
};

let transportPromise: Promise<SyncTransport> | null = null;
let workerReadyWait: Promise<ServiceWorker | null> | undefined;
let knownScriptURL: string | undefined;
let keepAliveTimer: ReturnType<typeof setInterval> | undefined;
let keepAliveInFlight: Promise<void> | null = null;

const rememberWorker = (worker: ServiceWorker) => {
	knownScriptURL = worker.scriptURL;
};

export const getActiveWorker = async (): Promise<ServiceWorker | null> => {
	if (!("serviceWorker" in navigator) || isPreviewMode || isMockMode) return null;

	try {
		if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;

		const registered = await navigator.serviceWorker.getRegistration();
		if (registered?.active) return registered.active;

		if (!workerReadyWait) {
			workerReadyWait = Promise.race([
				navigator.serviceWorker.ready,
				new Promise<null>((resolve) => {
					window.setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS);
				}),
			]).then((registration) => registration?.active ?? navigator.serviceWorker.controller ?? null);
		}

		return await workerReadyWait;
	} catch {
		return null;
	}
};

const postToWorker = (worker: ServiceWorker, message: SWMessage) =>
	new Promise<boolean>((resolve) => {
		const { port1, port2 } = new MessageChannel();
		const timer = window.setTimeout(() => {
			port1.onmessage = null;
			port1.close();
			resolve(false);
		}, SW_ACK_TIMEOUT_MS);

		port1.onmessage = () => {
			window.clearTimeout(timer);
			port1.onmessage = null;
			port1.close();
			resolve(true);
		};

		try {
			worker.postMessage(message, [port2]);
		} catch {
			window.clearTimeout(timer);
			port1.onmessage = null;
			port1.close();
			resolve(false);
		}
	});

const reviveWorker = async (): Promise<ServiceWorker | null> => {
	if (!("serviceWorker" in navigator) || isPreviewMode || isMockMode) return null;
	workerReadyWait = undefined;

	try {
		let registration = await navigator.serviceWorker.getRegistration();
		const scriptURL = knownScriptURL ?? registration?.active?.scriptURL ?? registration?.waiting?.scriptURL;

		if (registration) {
			try {
				await fetch(registration.scope, { method: "HEAD", cache: "no-store" });
			} catch {
				// fetch is only a wake-up attempt
			}

			try {
				await registration.update();
			} catch (error) {
				console.warn("[sync] service worker update failed", error);
			}
		}

		if (scriptURL) {
			try {
				registration = await navigator.serviceWorker.register(scriptURL, import.meta.env.DEV ? { type: "module" } : undefined);
			} catch (error) {
				console.warn("[sync] service worker register failed", error);
			}
		}

		const ready = await Promise.race([
			navigator.serviceWorker.ready,
			new Promise<null>((resolve) => {
				window.setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS);
			}),
		]);

		const worker = navigator.serviceWorker.controller ?? ready?.active ?? (await navigator.serviceWorker.getRegistration())?.active ?? null;

		if (worker) rememberWorker(worker);
		return worker;
	} catch (error) {
		console.warn("[sync] service worker revive failed", error);
		return null;
	}
};

const ensureWorkerAlive = async () => {
	if (keepAliveInFlight) return keepAliveInFlight;

	keepAliveInFlight = (async () => {
		const worker = await getActiveWorker();
		if (worker) {
			rememberWorker(worker);
			if (await postToWorker(worker, { type: "PING" })) return;
		}

		const revived = await reviveWorker();
		if (revived) await postToWorker(revived, { type: "PING" });
	})().finally(() => {
		keepAliveInFlight = null;
	});

	return keepAliveInFlight;
};

const startKeepAlive = () => {
	if (keepAliveTimer != null) return;

	const tick = () => {
		void ensureWorkerAlive();
	};

	keepAliveTimer = setInterval(tick, KEEP_ALIVE_INTERVAL_MS);
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") tick();
	});
	navigator.serviceWorker?.addEventListener("controllerchange", () => {
		const worker = navigator.serviceWorker.controller;
		if (worker) rememberWorker(worker);
	});
	tick();
};

const createWorkerTransport = (): SyncTransport => ({
	post: async (message) => {
		let worker = await getActiveWorker();
		if (worker) {
			rememberWorker(worker);
			if (await postToWorker(worker, message)) return;
		}

		worker = await reviveWorker();
		if (!worker || !(await postToWorker(worker, message))) {
			console.warn("[sync] failed to deliver message to service worker", message.type);
		}
	},
});

const createLocalTransport = async (): Promise<SyncTransport> => {
	const { dispatchSyncMessage } = await import("@innoclouds/service-worker/runtime");

	return {
		post: async (message) => {
			void dispatchSyncMessage(message).catch((error) => {
				console.error("[sync] local dispatch failed", message.type, error);
			});
		},
	};
};

const getTransport = () => {
	if (!transportPromise) {
		transportPromise = (async () => {
			const worker = await getActiveWorker();
			if (worker) {
				rememberWorker(worker);
				startKeepAlive();
				console.info("[sync] using service worker");
				return createWorkerTransport();
			}

			console.info("[sync] service worker unavailable, using main-thread fallback");
			return createLocalTransport();
		})();
	}

	return transportPromise;
};

const sendMessage = async (message: SWMessage) => {
	const transport = await getTransport();
	await transport.post(message);
};

const startPeriodicSync = (options: Options): PeriodicSync => {
	const timers = new Map<"menu" | "combos", ReturnType<typeof setTimeout>>();

	const schedule = (key: "menu" | "combos") => {
		const existing = timers.get(key);
		if (existing) clearTimeout(existing);

		timers.set(
			key,
			setTimeout(() => {
				void requestSync({ syncMenu: key === "menu", syncCombos: key === "combos" });
			}, SYNC_INTERVAL),
		);
	};

	const reschedule = (syncOptions?: SyncRequestOptions) => {
		if (syncOptions?.syncMenu !== false) schedule("menu");
		if (options.withCombos && syncOptions?.syncCombos !== false) schedule("combos");
	};

	const requestSync = async (syncOptions?: SyncRequestOptions) => {
		await sendMessage({
			type: "SYNC",
			orgId: options.orgId,
			domain: options.domain,
			options: syncOptions,
		});
		reschedule(syncOptions);
	};

	reschedule();

	return {
		requestSync,
		stop: () => {
			for (const timer of timers.values()) clearTimeout(timer);
			timers.clear();
		},
	};
};

export const useSyncWorker = async (options: Options) => {
	const periodic = startPeriodicSync(options);
	const unsubscribe = subscribeOrgUpdates((raw) => onOrgUpdate(raw, options, periodic));

	if (options.forceSync) {
		await periodic.requestSync();
	} else {
		await sendMessage({
			type: "PREFETCH_ORG",
			orgId: options.orgId,
			options: { withProducts: true, domain: options.domain, withCombos: !!options.withCombos },
		});
	}

	if (options.wsUrl) await useSyncSocket(options, periodic);

	const cleanup = () => {
		periodic.stop();
		void sendMessage({ type: "CANCEL_PREFETCH", orgId: options.orgId });
		unsubscribe();
	};

	window.addEventListener("beforeunload", cleanup);

	return { sendMessage };
};

const updateProductLikes = async (orgId: string, params?: Record<string, unknown>) => {
	const productId = typeof params?.product_id === "string" ? params.product_id : null;
	const likesCount = typeof params?.likes_count === "number" ? params.likes_count : null;
	if (!productId || likesCount === null) return;

	useLikesStore().setCount(productId, likesCount);
	await db.products.update([orgId, productId], { likes_count: likesCount });
};

const SOCKET_EMIT_METHODS = [
	"update_org_unit",
	"update_organization",
	"update_design_config",
	"update_decoration",
	"update_schedule",
	"update_badges",
] as const;

export const useSyncSocket = async (options: Options, periodic: PeriodicSync) => {
	if (!options.wsUrl) return;

	const wsUrl = `${options.wsUrl}/ws/org-unit/${options.orgId}/`;
	const ws = getWebSocketInstance(wsUrl, { heartbeatTimeout: 30_000 });

	const unsubscribers = [
		bindSocketStatusReporting(ws, "org-unit", wsUrl),
		ws.on("open", () => console.debug("Realtime WebSocket подключён (pointId:", options.orgId, ")")),
		ws.on("error", (error: unknown) => console.warn("WebSocket ошибка:", error)),
		ws.on("close", () => console.debug("Realtime WebSocket закрыт")),
		ws.on("message", (data: { method?: string; params?: Record<string, unknown> }) => {
			if (!data?.method) return;

			if (data.method === "update_menu") {
				void periodic.requestSync({ syncMenu: true, syncCombos: false });
				return;
			}

			if (data.method === "update_combos") {
				void periodic.requestSync({ syncMenu: false, syncCombos: true });
				return;
			}

			if (data.method === "update_likes") {
				void updateProductLikes(options.orgId, data.params);
				return;
			}

			if ((SOCKET_EMIT_METHODS as readonly string[]).includes(data.method)) emitter.emit(data.method, data.params);
		}),
	];

	const disconnect = () => {
		for (const unsub of unsubscribers) unsub();
		ws.close();
	};

	window.addEventListener("beforeunload", disconnect);

	ws.connect();
};

const onOrgUpdate = (raw: unknown, options: Options, periodic: PeriodicSync) => {
	const data = raw as BroadcastMessageData;
	if (!data?.type || data.orgId !== options.orgId) return;

	if (data.type === "PREFETCH_PROGRESS") return onPrefetchProgress(data);
	if (data.type === "PREFETCH_STATUS") return onPrefetchStatus(data, periodic);
	if (data.type === "MENU_UPDATE") return onMenuUpdated(data, options);
	if (data.type === "COMBO_UPDATE") return onComboUpdated(data, options);
	if (data.type === "ORG_UNIT_UPDATE") return emitter.emit("update_org_unit", data.params);
	if (data.type === "ORGANIZATION_UPDATE") return emitter.emit("update_organization", data.params);
	if (data.type === "DESIGN_CONFIG_UPDATE") return emitter.emit("update_design_config", data.params);
	if (data.type === "DECORATION_UPDATE") return emitter.emit("update_decoration", data.params);
	if (data.type === "SCHEDULE_UPDATE") return emitter.emit("update_schedule", data.params);
	if (data.type === "BADGES_UPDATE") return emitter.emit("update_badges", data.params);
};

const onPrefetchProgress = async (data: BroadcastMessageData) => {
	if (data.type !== "PREFETCH_PROGRESS") return;
	console.debug("[prefetch]", data.phase, data.done, "/", data.total);
};

const onPrefetchStatus = async (data: BroadcastMessageData, periodic: PeriodicSync) => {
	if (data.type !== "PREFETCH_STATUS") return;
	console.debug("[prefetch]", data.status);
	if (data.status === PrefetchStatus.COMPLETED) {
		await periodic.requestSync();
	}
};

const onMenuUpdated = async (data: BroadcastMessageData, options: Options) => {
	if (data.type !== "MENU_UPDATE") return;

	// console.debug("onMenuUpdated", data);

	const modulesStore = useModulesStore();
	const themeStore = useThemeStore();

	const anyUpdated =
		data.groups_added.length > 0 ||
		data.groups_updated.length > 0 ||
		data.products_added.length > 0 ||
		data.products_updated.length > 0 ||
		data.groups_deleted.length > 0 ||
		data.products_deleted.length > 0;

	if (!themeStore.data?.kiosk_hide_menu_update_notification && anyUpdated) {
		// @tolgee-key { key: 'hotmenu.notification.menu-update', ns: 'translation' }
		modulesStore.notificationData_lib.textNotification = i18next.t(`hotmenu.notification.menu-update`);
	}

	await updateCard(data);
	await updateCart(data);
	if (options.cacheMedia) cacheMediaInBackground(data);

	if (modulesStore.notificationData_lib?.textNotification?.length > 1) modulesStore.is_notification_lib = true;
};

const onComboUpdated = async (data: BroadcastMessageData, options: Options) => {
	if (data.type !== "COMBO_UPDATE") return;

	const hotmenu = useHotmenuStore();

	// console.debug("onComboUpdated", data);

	if (options.cacheMedia) {
		const combosIds = new Set(data.added.concat(data.updated));
		const combos = Array.from(hotmenu.allCombos.values()).filter((combo) => combosIds.has(combo.id));
		for (const combo of combos) {
			addToCache(combo.image_link);
			addToCache(combo.video_link);
		}
	}
};

const cacheMediaInBackground = async (data: BroadcastMessageData) => {
	if (data.type !== "MENU_UPDATE") return;

	const hotmenu = useHotmenuStore();

	const productsIds = new Set(data.products_added.concat(data.products_updated));
	const products = Array.from(hotmenu.products.values()).filter((product) => productsIds.has(product.id));
	for (const product of products) {
		addToCache(product.image_link);
		addToCache(product.video_link);
	}

	const groupsIds = new Set(data.groups_added.concat(data.groups_updated));
	const groups = Array.from(hotmenu.groups.values()).filter((group) => groupsIds.has(group.id));
	for (const group of groups) {
		addToCache(group.image_link);
		addToCache(group.video_link);
	}
};

const updateCard = async (data: BroadcastMessageData) => {
	if (data.type !== "MENU_UPDATE") return;

	// @TODO: Эта логика работает только на киоске
	// Для QR нужно либо редиректить в меню либо сделать BottomSheet для продукта, сейчас это не работает корректно
	const mainStore = useMainStore();
	const cardStore = useCardStore();
	const hotmenu = useHotmenuStore();

	const openCard = cardStore.stateCard.openCard;
	if (!openCard?.id) return;

	const product = hotmenu.products.get(openCard.id);

	if (!product || (product.adult && !mainStore.is18Allowed)) {
		cardStore.openProduct = { id: null, visible: false };
		cardStore.reset();
		emitter.emit("close-product");
		return;
	}

	if (product) InitConnection.create().fullCardProduct(product); // @TODO: Переделать на utils
};

const updateCart = async (data: BroadcastMessageData) => {
	if (data.type !== "MENU_UPDATE") return;

	const mainStore = useMainStore();
	const modulesStore = useModulesStore();
	const hotmenu = useHotmenuStore();

	const cart = mainStore.cartProducts_lib;
	const cartSet = new Set(cart.map((item) => item.id));
	const deletedProducts: any[] = [];
	const updatedCart: any[] = [];

	const freshData = Array.from(hotmenu.products.values()).filter((product) => cartSet.has(product.id));
	const freshMap = new Map(freshData.map((i) => [i.id, i]));

	for (const item of cart) {
		if (data.products_deleted.includes(item.id)) {
			deletedProducts.push(item);
			continue;
		}

		if (data.products_updated.includes(item.id)) {
			const product = freshMap.get(item.id);
			if (!product || (product.adult && !mainStore.is18Allowed)) {
				deletedProducts.push(item);
				continue;
			}

			if (product && item.size && !product.sizes.find((size) => size.id === item.size)) {
				deletedProducts.push(item);
				continue;
			}
		}

		updatedCart.push(item); // @TODO: Необходимо поля продукта в корзине
	}

	if (deletedProducts.length > 0) {
		const names = deletedProducts
			.map((p) => getLocaleName(p, i18next.language))
			.slice(0, 3)
			.join(", ");
		const diffCount = deletedProducts.length - 3;

		// @tolgee-key { key: 'hotmenu.notification.deleted-full', ns: 'translation' }
		// @tolgee-key { key: 'hotmenu.notification.deleted-less', ns: 'translation' }
		modulesStore.notificationData_lib.textNotification =
			diffCount > 0 ? i18next.t(`hotmenu.notification.deleted-full`, { names, count: diffCount }) : i18next.t(`hotmenu.notification.deleted-less`, { names });

		mainStore.cartProducts_lib = updatedCart;
		mainStore.updateCartInLocalStorage();
	}
};
